/**
 * Where the credentials live.
 *
 * The access token is kept **in memory only**. A clinic tablet spends its day
 * on one page, so surviving a reload matters less than not leaving a working
 * credential in `localStorage` for any injected script to read.
 *
 * The refresh token does persist, because something has to survive a reload and
 * an unattended TV board must come back on its own after a power cut. It is the
 * weaker link, and the backend is built knowing that: it rotates on every use,
 * and a token presented twice revokes every session for that user.
 *
 * A superadmin's chosen clinic follows the access token's lead: in memory only,
 * gone on reload. Reloading back into their own unscoped view is the safer
 * default for the one login that can reach every tenant, and "acting" already
 * reads as a deliberate, one-screen-at-a-time thing rather than a standing
 * setting — asking again after a reload costs one click.
 */

const REFRESH_KEY = "livequeue.refresh";

let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export interface ActingClinic {
  id: string;
  name: string;
}

const ACTING_KEY = "livequeue.acting";

/**
 * Restored from storage, not started empty.
 *
 * The refresh token already carries the acting clinic — and every silent
 * refresh re-sends it by reading this value. Held only in memory, a page reload
 * lost it, and the next refresh quietly dropped a superadmin back into their own
 * clinic-less view with the banner gone and every screen answering 404. The
 * token and the thing that describes it now live in the same place.
 */
function restore(): ActingClinic | null {
  try {
    const stored = localStorage.getItem(ACTING_KEY);
    return stored ? (JSON.parse(stored) as ActingClinic) : null;
  } catch {
    // Private browsing, disabled storage, or a value from an older shape.
    return null;
  }
}

let actingClinic: ActingClinic | null = restore();
const actingClinicListeners = new Set<() => void>();

export function getActingClinic(): ActingClinic | null {
  return actingClinic;
}

/** For `useActingClinic` — so the "Managing X" banner re-renders on change
 *  without every page needing to poll or thread the value through props. */
export function subscribeActingClinic(listener: () => void): () => void {
  actingClinicListeners.add(listener);
  return () => {
    actingClinicListeners.delete(listener);
  };
}

export function setActingClinic(clinic: ActingClinic | null): void {
  actingClinic = clinic;
  try {
    if (clinic) localStorage.setItem(ACTING_KEY, JSON.stringify(clinic));
    else localStorage.removeItem(ACTING_KEY);
  } catch {
    // Storage is a convenience here: the session still works for this tab.
  }
  actingClinicListeners.forEach((listener) => { listener(); });
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    // Private browsing, or storage disabled. The session still works until the
    // tab is closed, which is enough to finish a shift.
    return null;
  }
}

export function storeSession(access: string, refresh: string): void {
  accessToken = access;
  try {
    localStorage.setItem(REFRESH_KEY, refresh);
  } catch {
    /* see getRefreshToken */
  }
}

export function clearSession(): void {
  accessToken = null;
  setActingClinic(null);
  try {
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* see getRefreshToken */
  }
}
